import { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Kudo } from '../../types';

interface KudosGridProps {
  kudos: Kudo[];
}

export default function KudosGrid({ kudos }: KudosGridProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const itemsPerSlide = 4;
  const totalSlides = Math.ceil(kudos.length / itemsPerSlide);
  
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };
  
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };
  
  const openZoom = (index: number) => {
    setSelectedIndex(index);
  };
  
  const closeZoom = () => {
    setSelectedIndex(null);
  };
  
  const nextKudo = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex + 1) % kudos.length);
    }
  };
  
  const prevKudo = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex - 1 + kudos.length) % kudos.length);
    }
  };
  
  const startIndex = currentSlide * itemsPerSlide;
  const displayedKudos = kudos.slice(startIndex, startIndex + itemsPerSlide);

  return (
    <>
      {/* Carousel */}
      <div className="relative">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {displayedKudos.map((kudo, idx) => {
            const actualIndex = startIndex + idx;
            return (
              <button
                key={kudo.id}
                onClick={() => openZoom(actualIndex)}
                className="group relative aspect-[4/3] overflow-hidden rounded-lg border border-gray-200 bg-white transition-all hover:shadow-lg"
              >
                <img
                  src={kudo.imageUrl}
                  alt={kudo.alt}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            );
          })}
        </div>
        
        {/* Carousel Navigation */}
        {totalSlides > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <button
              onClick={prevSlide}
              className="rounded-lg bg-white p-2 shadow-sm transition-all hover:shadow-md disabled:opacity-50"
              disabled={currentSlide === 0}
              aria-label="Previous kudos"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: totalSlides }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`h-2 rounded-full transition-all ${
                    idx === currentSlide ? 'w-8 bg-primary-600' : 'w-2 bg-gray-300'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
            
            <button
              onClick={nextSlide}
              className="rounded-lg bg-white p-2 shadow-sm transition-all hover:shadow-md disabled:opacity-50"
              disabled={currentSlide === totalSlides - 1}
              aria-label="Next kudos"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
        
        <p className="mt-2 text-center text-sm text-gray-600">
          Showing {startIndex + 1}-{Math.min(startIndex + itemsPerSlide, kudos.length)} of {kudos.length} kudos
        </p>
      </div>
      
      {/* Zoomed View Modal */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeZoom}
        >
          <button
            className="absolute right-4 top-4 rounded-lg bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
            onClick={closeZoom}
            aria-label="Close"
          >
            <X size={24} />
          </button>
          
          {/* Previous Button */}
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-lg bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              prevKudo();
            }}
            aria-label="Previous kudo"
          >
            <ChevronLeft size={32} />
          </button>
          
          {/* Image */}
          <div className="relative" onClick={e => e.stopPropagation()}>
            <img
              src={kudos[selectedIndex].imageUrl}
              alt={kudos[selectedIndex].alt}
              className="max-h-[85vh] max-w-[85vw] rounded-lg"
            />
            <div className="mt-4 text-center">
              <p className="text-sm text-white/80">
                {selectedIndex + 1} / {kudos.length}
              </p>
              <p className="mt-1 text-white">{kudos[selectedIndex].alt}</p>
            </div>
          </div>
          
          {/* Next Button */}
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-lg bg-white/10 p-3 text-white transition-colors hover:bg-white/20"
            onClick={(e) => {
              e.stopPropagation();
              nextKudo();
            }}
            aria-label="Next kudo"
          >
            <ChevronRight size={32} />
          </button>
        </div>
      )}
    </>
  );
}
